import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ntxAPI } from '../../../API/axiosNeatex';

interface TaskComment {
  id: string;
  taskId: string;
  commentText: string;
  personName?: string;
  personName2?: string;
  creationDate?: string;
}

// ========== Async Thunks ==========

// 🟢 إضافة تعليق
export const addTaskComment = createAsyncThunk(
  'TaskComment/addTaskComment',
  async (item: Omit<TaskComment, 'id' | 'createdBy' | 'createdAt' | 'updatedAt'>, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post('Task/AddTaskComment', item);
      if (data?.succeeded) return data.data;
      return rejectWithValue(data?.message || 'فشل في إضافة التعليق');
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// 🟡 تحديث تعليق
export const updateTaskComment = createAsyncThunk(
  'TaskComment/updateTaskComment',
  async (item: TaskComment, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post('Task/UpdateTaskComment', item);
      if (data?.succeeded) return data.data;
      return rejectWithValue(data?.message || 'فشل في تحديث التعليق');
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// 🔵 جلب التعليقات حسب المهمة
export const getTaskCommentByTaskId = createAsyncThunk(
  'TaskComment/getTaskCommentByTaskId',
  async (taskId: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.get(`Task/GetTaskCommentByTaskId?taskId=${taskId}`);
      if (data?.succeeded) return data.data;
      return [];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// 🔴 حذف تعليق
export const deleteTaskComment = createAsyncThunk(
  'TaskComment/deleteTaskComment',
  async (commentId: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post(`Task/DeleteTaskComment?Id=${commentId}`);
      if (data?.succeeded) return commentId;
      return rejectWithValue(data?.message || 'فشل في حذف التعليق');
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ========== Initial State ==========
const initialState: {
  taskCommentList: TaskComment[];
  loading: boolean;
  error: string | null;
} = {
  taskCommentList: [],
  loading: false,
  error: null
};

// ========== Slice ==========
const TaskCommentSlice = createSlice({
  name: 'TaskComment',
  initialState,
  reducers: {
    clearTaskComments: state => {
      state.taskCommentList = [];
    },
    addLocalTaskComment: (state, action: PayloadAction<TaskComment>) => {
      state.taskCommentList.unshift(action.payload);
    },
    updateLocalTaskComment: (state, action: PayloadAction<TaskComment>) => {
      const index = state.taskCommentList.findIndex(c => c.id === action.payload.id);
      if (index !== -1) state.taskCommentList[index] = action.payload;
    }
  },
  extraReducers: builder => {
    builder
      // 🔵 Get
      .addCase(getTaskCommentByTaskId.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTaskCommentByTaskId.fulfilled, (state, action) => {
        state.loading = false;
        state.taskCommentList = action.payload;
      })
      .addCase(getTaskCommentByTaskId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // 🟢 Add
      .addCase(addTaskComment.fulfilled, (state, action) => {
        state.taskCommentList.unshift(action.payload);
      })
      .addCase(addTaskComment.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // 🟡 Update
      .addCase(updateTaskComment.fulfilled, (state, action) => {
        const index = state.taskCommentList.findIndex(c => c.id === action.payload.id);
        if (index !== -1) state.taskCommentList[index] = action.payload;
      })
      .addCase(updateTaskComment.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // 🔴 Delete
      .addCase(deleteTaskComment.fulfilled, (state, action) => {
        state.taskCommentList = state.taskCommentList.filter(c => c.id !== action.payload);
      })
      .addCase(deleteTaskComment.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  }
});

export const { clearTaskComments, addLocalTaskComment, updateLocalTaskComment } = TaskCommentSlice.actions;

export default TaskCommentSlice.reducer;
